import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, notification, Form, Modal, Select, Space,Tooltip,Input } from "antd";
import GenericTable from "../components/GenericTable";
import GenericDrawer from "../components/GenericModal";
import GenericViewModal from "../components/GenericViewModal";
import {
  fetchHcpcsRequest,
  createHcpcRequest,
  updateHcpcRequest,
  deleteHcpcRequest,
  fetchHcpcRulesRequest,
  mapHcpcToRuleRequest,
  deleteHcpcRuleMappingRequest
} from "../redux-saga/actions/hcpcActions";
import ProjectNavbar from "../components/ProjectNavbar";
import { useParams } from "react-router-dom";
import { fetchRulesRequest } from "../redux-saga/actions/ruleActions";
import moment from "moment";
import { updateCurrentProjectId } from "../redux-saga/actions/customerActions";
import { EyeOutlined, EditOutlined, DeleteOutlined,FileSearchOutlined } from "@ant-design/icons";
import {IconReport} from '@tabler/icons-react';

const { Option } = Select;

const hcpcModalInputs = [
  { label: "HCPC ID", name: "hcpcId", type: "text" },
  { label: "Code", name: "code", type: "text" },
  { label: "Description", name: "description", type: "text" },
  { label: "Effective Date", name: "effectiveDate", type: "date" },
  { label: "Deleted Date", name: "deletedDate", type: "date" }
];

const HcpcPage = () => {
  const dispatch = useDispatch();
  const hcpcs = useSelector((state) => state.hcpc.hcpcs?.data);
  const rules = useSelector((state) => state.hcpc.rules?.data);
  const commonRules = useSelector((state) => state.rule.rules?.data);
  const { projectId } = useParams();
  const loading = useSelector((state) => state.hcpc.loading);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingHcpc, setEditingHcpc] = useState(null);
  const [form] = Form.useForm();
  const [viewingHcpc, setViewingHcpc] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isRulesModalVisible, setIsRulesModalVisible] = useState(false);
  const [selectedHcpc, setSelectedHcpc] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);

  const metaData = useSelector((state) => state.hcpc.hcpcs?.meta || {});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchHcpcsRequest({ page: currentPage, limit: 2, search: searchTerm }));
    dispatch(fetchRulesRequest(projectId));
    dispatch(updateCurrentProjectId(projectId));
  }, [dispatch, projectId,currentPage, searchTerm]);

  const handleCreateOrEdit = (values) => {
    if (editingHcpc) {
      dispatch(updateHcpcRequest(editingHcpc.id, values));
    } else {
      dispatch(createHcpcRequest(values));
    }
    notification.success({ message: "Success", description: "HCPC saved successfully." });
    setIsModalVisible(false);
    setEditingHcpc(null);
    form.resetFields();
  };

  const handleView = (record) => {
    const formattedRecord = {
      ...record,
      createdAt:record.createdAt ? moment(record.createdAt).format("YYYY-MM-DD HH:mm:ss") : null,
      updatedAt:record.updatedAt ? moment(record.updatedAt).format("YYYY-MM-DD HH:mm:ss") : null,
      effectiveDate:record.effectiveDate ? moment(record.effectiveDate).format("YYYY-MM-DD HH:mm:ss") : null,
      deletedDate:record.deletedDate ? moment(record.deletedDate).format("YYYY-MM-DD HH:mm:ss") : null,
    }
    setViewingHcpc(formattedRecord);
    setIsViewModalVisible(true);
  };

  const handleEdit = (record) => {
    const formattedRecord = {
      ...record,
      effectiveDate: record.effectiveDate ? moment(record.effectiveDate) : null,
      deletedDate: record.deletedDate ? moment(record.deletedDate) : null,
    };
    setEditingHcpc(formattedRecord);
    setIsModalVisible(true);
    form.setFieldsValue(formattedRecord);
  };

  const handleDelete = (record) => {
   Modal.confirm({
      title: "Confirm",
      content: "Are you sure you want to delete this HCPC?",
      onOk: () => {
        dispatch(deleteHcpcRequest(record.id));
        notification.success({ message: "Success", description: "HCPC deleted successfully." });
      },
    });
  };

  // Fetch rules associated with an HCPC
  const handleViewRules = (record) => {
    setViewingHcpc(record);
    setSelectedHcpc(record.id);
    dispatch(fetchHcpcRulesRequest(projectId, record.id));
    setIsRulesModalVisible(true);
  };

  // Map a selected rule to the selected HCPC
  const handleMapRule = () => {
    if (selectedRule) {
      dispatch(mapHcpcToRuleRequest(projectId, viewingHcpc.id, selectedRule));
      notification.success({ message: "Success", description: "Rule mapped to HCPC successfully." });
      setSelectedRule(null);
      dispatch(fetchHcpcRulesRequest(projectId, viewingHcpc.id));
    } else {
      notification.warning({ message: "Warning", description: "Please select a rule to map." });
    }
  };

  // Delete a rule mapping
  const handleDeleteRuleMapping = (ruleId) => {
    dispatch(deleteHcpcRuleMappingRequest(projectId, viewingHcpc.id, ruleId));
    notification.success({ message: "Success", description: "Rule mapping deleted successfully." });
    dispatch(fetchHcpcRulesRequest(projectId, viewingHcpc.id));
  };

  const columnsConfig = [
    { title: "HCPC ID", dataIndex: "hcpcId" },
    { title: "Code", dataIndex: "code" },
    { title: "Description", dataIndex: "description" },
    { title: "Effective Date", dataIndex: "effectiveDate" },
    { title: "Deleted Date", dataIndex: "deletedDate" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <EyeOutlined style={{ fontSize: "20px", color: "#1890ff" }} onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <EditOutlined style={{ fontSize: "20px", color: "#faad14" }} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined style={{ fontSize: "20px", color: "#f5222d" }} onClick={() => handleDelete(record)} />
          </Tooltip>
          <Tooltip title="View Rule">
          <FileSearchOutlined style={{ fontSize: "20px", color: "#f5222d" }} onClick={() => handleViewRules(record)} />  
      </Tooltip>
        </Space>
      ),
    },
  
  ];
  

  const ruleColumnsConfig = [
    { title: "Rule ID", dataIndex: "ruleId" },
    { title: "Content", dataIndex: "content" },
    {
      title: "Actions",
      render: (_, rule) => (
        <Button onClick={() => handleDeleteRuleMapping(rule.id)}>Delete Mapping</Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", gap: "10px" }}>
     <ProjectNavbar />
      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                <IconReport style={{ fontSize: "24px", color: "black" }} />
                <h2 style={{ margin: 0 }}>HCPC</h2>
            </div>

            <div style={{ display: "flex", justifyContent: "end", marginBottom: 11 }}>
              <Button type="primary" onClick={() => setIsModalVisible(true)}>
                Create HCPC
              </Button>
            </div>
        </div>
        <Input
          placeholder="Search ICD"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: "16px" }}
        />
        <GenericTable data={hcpcs} columnsConfig={columnsConfig}  loading={loading} pagination={{
            current: metaData.page || 1,
            pageSize: metaData.limit || 2,
            total: metaData.total || 0,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              dispatch(fetchHcpcsRequest({ page, limit: pageSize, search: searchTerm }));
            },
          }}  />

        {/* HCPC Create/Edit Drawer */}
        {isModalVisible && (
          <GenericDrawer
            isVisible={isModalVisible}
            handleCancel={() => {
              setIsModalVisible(false);
              setEditingHcpc(null);
            }}
            title={
              <span style={{display : "flex",alignItems:"center"}}>
                <IconReport style={{ color: "white", marginRight: "8px" }} />
                {editingHcpc ? "Edit HCPC" : "Create HCPC"}
              </span>
            }
            inputs={hcpcModalInputs}
            onSubmit={handleCreateOrEdit}
            predefinedData={editingHcpc || {}}
          />
        )
      }
        {/* HCPC View Modal */}
        {isViewModalVisible && (
          <GenericViewModal
            isVisible={isViewModalVisible}
            handleCancel={() => setIsViewModalVisible(false)}
            title="View HCPC"
            data={viewingHcpc}
            footerButtons={["Close"]}
            onButtonClick={() => setIsViewModalVisible(false)}
          />
        )}

        {/* Rules Modal */}
        <Modal
          title="HCPC Rules"
          visible={isRulesModalVisible}
          onCancel={() => setIsRulesModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setIsRulesModalVisible(false)}>
              Close
            </Button>,
          ]}
        >
          <div style={{ display: "flex", gap: "10px" }}>
            <Select
              style={{ width: "100%", marginTop: 16, marginBottom: "10px" }}
              placeholder="Select Rule to Map"
              onChange={(value) => setSelectedRule(value)}
            >
              {commonRules?.map((rule) => (
                <Option key={rule.id} value={rule.id}>
                  {rule.ruleId}
                </Option>
              ))}
            </Select>
            <Button type="primary" style={{ marginTop: 16 }} onClick={handleMapRule}>
              Map Selected Rule
            </Button>
          </div>
          <GenericTable data={rules} columnsConfig={ruleColumnsConfig} loading={loading} />
        </Modal>
      </div>
    </div>
  );
};

export default HcpcPage;
